import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../hooks/useAuth';

// Функция для форматирования телефона в простой формат +7XXXXXXXXXX
const formatPhone = (value: string) => {
  if (!value) return '';
  
  // Убираем все символы кроме цифр
  const digits = value.replace(/\D/g, '');
  
  // Если номер начинается с 8, заменяем на 7
  let normalizedDigits = digits.startsWith('8') ? '7' + digits.slice(1) : digits;
  
  // Если номер не начинается с 7, добавляем 7
  if (!normalizedDigits.startsWith('7') && normalizedDigits.length > 0) {
    normalizedDigits = '7' + normalizedDigits;
  }
  
  // Ограничиваем длину до 11 цифр
  normalizedDigits = normalizedDigits.slice(0, 11);
  
  // Возвращаем номер в простом формате +7XXXXXXXXXX
  if (normalizedDigits.length === 0) return '';
  return `+${normalizedDigits}`;
};

interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  phone: string;
  agreeToTerms: boolean;
}

const RegisterPage: React.FC = () => {
  const { register: registerTrainer, isRegisterLoading, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<RegisterForm>({
    mode: 'onChange' // Валидация при каждом изменении
  });

  const password = watch('password');
  
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhone(e.target.value);
    setValue('phone', formattedPhone);
  };

  // Функция для получения часового пояса из браузера
  const getBrowserTimezone = (): string => {
    try {
      // Пытаемся получить часовой пояс из браузера
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Если получили валидный часовой пояс, возвращаем его
      if (timezone && timezone !== '') {
        return timezone;
      }
    } catch (error) {
      console.warn('Не удалось определить часовой пояс браузера:', error);
    }
    
    // Возвращаем часовой пояс по умолчанию
    return 'Europe/Moscow';
  };

  // Если уже авторизован, перенаправляем на дашборд
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = (data: RegisterForm) => {
    const { confirmPassword, ...registerData } = data;
    
    // Добавляем часовой пояс к данным регистрации
    const registrationData = {
      ...registerData,
      timezone: getBrowserTimezone()
    };
    
    registerTrainer(registrationData);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Регистрация тренера
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Или{' '}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              войдите в существующий аккаунт
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                  Имя
                </label>
                <input
                  id="first_name"
                  type="text"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Имя"
                  {...register('first_name', {
                    required: 'Имя обязательно',
                    minLength: {
                      value: 2,
                      message: 'Имя должно быть не менее 2 символов'
                    }
                  })}
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                  Фамилия
                </label>
                <input
                  id="last_name"
                  type="text"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Фамилия"
                  {...register('last_name', {
                    required: 'Фамилия обязательна',
                    minLength: {
                      value: 2,
                      message: 'Фамилия должна быть не менее 2 символов'
                    }
                  })}
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email адрес
              </label>
              <input
                id="email"
                type="email"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Введите email"
                {...register('email', {
                  required: 'Email обязателен',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Некорректный email адрес'
                  }
                })}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Телефон
              </label>
              <input
                id="phone"
                type="tel"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="+79001234567"
                {...register('phone', {
                  onChange: handlePhoneChange,
                  required: 'Телефон обязателен',
                  validate: (value) => {
                    if (!value || value.trim() === '') return 'Телефон обязателен';
                    const digits = value.replace(/\D/g, '');
                    if (!digits.startsWith('7')) return 'Номер телефона должен начинаться с +7';
                    if (digits.length !== 11) return 'Номер телефона должен содержать ровно 10 цифр после +7';
                    return true;
                  }
                })}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Введите 10 цифр номера, +7 будет добавлено автоматически
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Пароль
              </label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Введите пароль"
                {...register('password', {
                  required: 'Пароль обязателен',
                  minLength: {
                    value: 6,
                    message: 'Пароль должен быть не менее 6 символов'
                  }
                })}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Подтверждение пароля
              </label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Подтвердите пароль"
                {...register('confirmPassword', {
                  required: 'Подтверждение пароля обязательно',
                  validate: (value) => value === password || 'Пароли не совпадают'
                })}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="agreeToTerms"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                {...register('agreeToTerms', {
                  required: 'Необходимо согласиться с политиками'
                })}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="agreeToTerms" className="text-gray-700">
                Соглашаюсь с{' '}
                <a 
                  href="https://trainerapp.v13.ru/privacy" 
                  className="text-blue-600 hover:text-blue-500 underline"
                >
                  политикой конфиденциальности
                </a>
                ,{' '}
                <a 
                  href="https://trainerapp.v13.ru/terms" 
                  className="text-blue-600 hover:text-blue-500 underline"
                >
                  условиями использования
                </a>
                {' '}и{' '}
                <a 
                  href="https://trainerapp.v13.ru/cookies" 
                  className="text-blue-600 hover:text-blue-500 underline"
                >
                  политикой cookies
                </a>
              </label>
              {errors.agreeToTerms && (
                <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isRegisterLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRegisterLoading ? 'Регистрация...' : 'Зарегистрироваться'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage; 